import { useCallback, useRef } from "react";
import type { RefObject } from "react";

/**
 * How far the pointer must travel before a press becomes a pan rather than a click.
 *
 * The list-table rows are clickable, so this threshold is what keeps the two gestures apart:
 * below it nothing happens and the row's own click runs normally; above it we pan and swallow
 * the click that the browser fires afterwards.
 */
const DRAG_THRESHOLD_PX = 5;

/**
 * Click-and-drag horizontal panning for a scroll container.
 *
 * macOS overlay scrollbars reserve no layout space and hide when idle, so a table that scrolls
 * sideways has no bar to grab — the only built-in gestures are a trackpad swipe or shift+wheel,
 * neither of which is discoverable. This makes the obvious gesture work.
 *
 * Mouse only: touch and trackpad already pan natively, and intercepting those would fight the
 * browser's momentum scrolling.
 *
 * Spread the result onto the same element whose ref is passed in:
 *
 * ```tsx
 * const { ref, edges, onScroll } = useScrollEdges<HTMLDivElement>([rows.length]);
 * const dragProps = useDragScroll(ref);
 * <div ref={ref} onScroll={onScroll} {...dragProps} className="overflow-auto">
 * ```
 */
export function useDragScroll<T extends HTMLElement>(ref: RefObject<T | null>) {
  const drag = useRef({
    pointerId: -1,
    startX: 0,
    startScrollLeft: 0,
    active: false,
  });
  // Set when a pan ends, consumed by the click the browser fires straight afterwards.
  const swallowNextClick = useRef(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current;
      if (!el) return;
      // Clear here rather than after consuming it: a pan that ends outside the container never
      // produces a click, and a stale flag would eat the next legitimate one.
      swallowNextClick.current = false;
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      if (el.scrollWidth <= el.clientWidth) return; // nothing to pan
      drag.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startScrollLeft: el.scrollLeft,
        active: false,
      };
    },
    [ref]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current;
      const d = drag.current;
      if (!el || d.pointerId !== e.pointerId) return;

      const dx = e.clientX - d.startX;
      if (!d.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        d.active = true;
        el.setPointerCapture(e.pointerId);
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
      }
      el.scrollLeft = d.startScrollLeft - dx;
    },
    [ref]
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current;
      const d = drag.current;
      if (!el || d.pointerId !== e.pointerId) return;

      if (d.active) {
        swallowNextClick.current = true;
        if (el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId);
        }
        el.style.cursor = "";
        el.style.userSelect = "";
      }
      d.pointerId = -1;
      d.active = false;
    },
    [ref]
  );

  // Capture phase so this runs before the row's own onClick handler can navigate.
  const onClickCapture = useCallback((e: React.MouseEvent<T>) => {
    if (!swallowNextClick.current) return;
    swallowNextClick.current = false;
    e.stopPropagation();
    e.preventDefault();
  }, []);

  // Stops the browser's native text/link drag from hijacking the pan.
  const onDragStart = useCallback((e: React.DragEvent<T>) => {
    if (drag.current.active) e.preventDefault();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClickCapture,
    onDragStart,
  };
}
